
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  Autocomplete,
  Grid
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, RefreshCw, Search as SearchIcon, FileText as ModelsLogoIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';

export default function ModulisticaList() {
  const [modelli, setModelli] = useState([]);
  const [filteredModelli, setFilteredModelli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  
  // Filters
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchModelli();
    fetchCategories();
  }, []);

  useEffect(() => {
    extractUsersFromModels();
  }, [modelli]);

  useEffect(() => {
    filterAndSortModelli();
  }, [modelli, searchTerm, orderBy, order, selectedCategory, selectedUser]);

  const fetchCategories = async () => {
    try {
      const catRes = await api.get('/categorie-utilizzo');
      setCategories(catRes.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const extractUsersFromModels = () => {
    if (modelli.length > 0) {
      const uniqueUsers = [];
      const userMap = new Map();
      
      modelli.forEach(modello => {
        if (modello.user_id && modello.user_name && !userMap.has(modello.user_id)) {
          userMap.set(modello.user_id, true);
          uniqueUsers.push({
            id: modello.user_id,
            name: modello.user_name
          });
        }
      });
      
      // Sort users alphabetically
      uniqueUsers.sort((a, b) => a.name.localeCompare(b.name));
      setUsers(uniqueUsers);
    }
  };

  const fetchModelli = async () => {
    setLoading(true);
    try {
      const response = await api.get('/modulistica');
      setModelli(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching modelli:', err);
      setError('Failed to load data');
      toast.error('Errore nel caricamento dei modelli');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortModelli = () => {
    let result = [...modelli];

    // Filter by Search Term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(item => 
        (item.nome_modello && item.nome_modello.toLowerCase().includes(lowerSearch)) ||
        (item.categorie_nomi && item.categorie_nomi.toLowerCase().includes(lowerSearch)) ||
        (item.user_name && item.user_name.toLowerCase().includes(lowerSearch))
      );
    }

    // Filter by Category
    if (selectedCategory) {
      result = result.filter(item => 
        item.categorie_ids && item.categorie_ids.includes(selectedCategory.id)
      );
    }

    // Filter by User
    if (selectedUser) {
      result = result.filter(item => 
        item.user_id === selectedUser.id
      );
    }

    // Sort
    result.sort((a, b) => {
      let valueA = a[orderBy];
      let valueB = b[orderBy];

      // Handle null/undefined
      if (valueA === null || valueA === undefined) valueA = '';
      if (valueB === null || valueB === undefined) valueB = '';

      // Special handling for dates if needed, but string comparison works for ISO dates
      if (orderBy === 'id') {
        valueA = Number(valueA);
        valueB = Number(valueB);
      }

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredModelli(result);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo modello?')) {
      try {
        await api.delete(`/modulistica/${id}`);
        toast.success('Modello eliminato');
        fetchModelli();
      } catch (err) {
        console.error('Error deleting modello:', err);
        toast.error('Errore durante l\'eliminazione');
      }
    }
  };

  // Pagination calculation
  const paginatedModelli = filteredModelli.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <ModelsLogoIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Modelli Documentali
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Gestisci i modelli di documento per la generazione automatica
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            size="small"
            onClick={fetchModelli}
            title="Ricarica"
          >
            <RefreshCw size={16} />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/modulistica/modelli/new')}
          >
            <PlusIcon size={16} style={{ marginRight: 8 }} />
            Nuovo Modello
          </Button>
        </div>
      </div>

      {error && <Alert severity="error">{error}</Alert>}

      <Card className="shadow-md border-none overflow-hidden rounded-xl">
        <Box className="p-4 border-b border-gray-100">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                placeholder="Cerca per nome, categoria o creatore..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className="h-4 w-4 text-gray-400" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={categories}
                getOptionLabel={(option) => option.nome_categoria}
                value={selectedCategory}
                onChange={(event, newValue) => setSelectedCategory(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Filtra per Categoria" variant="outlined" size="small" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.name || option.email}
                value={selectedUser}
                onChange={(event, newValue) => setSelectedUser(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Filtra per Utente" variant="outlined" size="small" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
          </Grid>
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'nome_modello'}
                    direction={orderBy === 'nome_modello' ? order : 'asc'}
                    onClick={() => handleRequestSort('nome_modello')}
                  >
                    Nome Modello
                  </TableSortLabel>
                </TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_validita'}
                    direction={orderBy === 'data_validita' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_validita')}
                  >
                    Data Validità
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'user_name'}
                    direction={orderBy === 'user_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_name')}
                  >
                    Creato Da
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8">
                    <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredModelli.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8 text-gray-500">
                    Nessun modello trovato
                  </TableCell>
                </TableRow>
              ) : (
                paginatedModelli.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell className="font-medium">{row.nome_modello}</TableCell>
                    <TableCell>
                      {row.categorie_nomi ? (
                        <div className="flex flex-wrap gap-1">
                          {row.categorie_nomi.split(', ').map((cat, idx) => (
                            <Chip key={idx} label={cat} size="small" variant="outlined" />
                          ))}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {row.data_validita ? new Date(row.data_validita).toLocaleDateString('it-IT') : '-'}
                    </TableCell>
                    <TableCell>{row.user_name || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/modulistica/modelli/${row.id}`)}
                        sx={{ color: 'text.primary' }}
                      >
                        <EditIcon size={18} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                        <DeleteIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredModelli.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </Card>
    </Box>
  );
}
